import React, { JSX, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { DialogFrame, useDialog } from "../../../components/Dialogs/dialog-base";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { isUploadRejected } from "../../../../utils/functions/productPending";
import { uploadRejectedTextKey } from "../../../../utils/functions/uploadRejected";

/**
 * "The file was rejected" (flag REACT_APP_UPLOAD_PENDING_ENABLED, design
 * 14/9 §5.6). Opens itself ONCE per logo id the moment the open logo carries
 * uploadFailedReason (the worker's deterministic verdict, delivered by the
 * editor's re-read <= 5 s after it), explains the reason per code (the
 * supplier's own text for the generic one) and offers "Upload another file"
 * (the caller removes the dead row and opens the New logo dialog) or "Back
 * to logos" (the caller removes the row and leaves). Closing with the X
 * keeps the row and the locks - the badge still says why. Renders nothing
 * (no Modal, no state effect) while the logo is not rejected; the call sites
 * only mount it under the flag, so the flag-off tree is unchanged.
 */
export function UploadRejectedDialog({
  logo,
  onUploadAnother,
  onBack,
}: {
  logo: BaseLogo | null | undefined;
  onUploadAnother?: () => void;
  onBack?: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const dialog = useDialog();
  const rejected = isUploadRejected(logo);
  const logoId = logo?.id ?? 0;
  const shownFor = useRef(0);
  const { openDialog } = dialog;
  useEffect(() => {
    if (!rejected || !logoId || shownFor.current === logoId) return;
    shownFor.current = logoId;
    openDialog();
  }, [rejected, logoId, openDialog]);
  if (!rejected || !logo) return null;
  const text = t(
    uploadRejectedTextKey(logo.uploadFailedReason, logo.uploadFailedMessage),
    { message: logo.uploadFailedMessage ?? "" }
  );
  return (
    <DialogFrame dialogOption={dialog}>
      <div
        className={"flex flex-col space-y-4 p-4 max-w-[480px]"}
        data-testid="upload-rejected-dialog"
        role="alertdialog"
        aria-labelledby="upload-rejected-title"
      >
        <div className={"flex justify-center text-status-failed-1"}>
          <FontAwesomeIcon size={"3x"} icon={faTriangleExclamation} />
        </div>
        <p
          id="upload-rejected-title"
          className={"text-center text-base font-bold"}
        >
          {t("upload_rejected_title")}
        </p>
        <p className={"text-center text-sm whitespace-pre-line"}>{text}</p>
        {logo.uploadFailedMessage && (
          <p
            className={"text-center text-xs text-gray-500 break-words"}
            title={logo.uploadFailedMessage}
          >
            {logo.uploadFailedMessage}
          </p>
        )}
        <div className={"flex justify-center space-x-4"}>
          <PrimaryButton
            width={"md"}
            onClick={() => {
              dialog.closeDialog();
              onUploadAnother?.();
            }}
          >
            {t("upload_rejected_upload_another")}
          </PrimaryButton>
          <SecondaryButton
            width={"md"}
            className={"button-secondary"}
            onClick={() => {
              dialog.closeDialog();
              onBack?.();
            }}
          >
            {t("upload_rejected_back")}
          </SecondaryButton>
        </div>
      </div>
    </DialogFrame>
  );
}
